import type { ElementRef } from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import Spectrum from 'spectrum-vanilla';
import type { Instance } from 'tinycolor2';

import { trackByIndex } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import {
  DropdownComponent,
  DropdownItemComponent,
  DropdownWidth,
} from '@core/user-interface-legacy';

@Component({
  selector: 'rt-color-menu',
  templateUrl: 'color-menu.component.html',
  styleUrls: ['color-menu.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [DropdownComponent, DropdownItemComponent, TranslatePipe],
})
export class ColorMenuComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdRef = inject(ChangeDetectorRef);

  public readonly DropdownWidth = DropdownWidth;
  public readonly trackByIndex = trackByIndex;

  public close: () => void;
  public reset: () => void;
  public colorChanged: (value: string | null) => void;

  @ViewChild('colorInput') public colorInputEl: ElementRef<HTMLInputElement>;

  public presetColors: string[] = [];
  public historyColors: string[] = [];
  public value: string = '#5a5f72';

  private pickerInstance: Spectrum;
  private valueChange$ = new Subject<string>();

  public ngOnInit() {
    this.valueChange$
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.value = value;
        this.colorChanged(value);
      });
  }

  public ngAfterViewInit() {
    if (!this.colorInputEl.nativeElement) return;
    this.pickerInstance = Spectrum.create(this.colorInputEl.nativeElement, {
      type: 'flat',
      preferredFormat: 'hex',
      showInput: true,
      showAlpha: false,
      showButtons: false,
      showPalette: false,
      allowEmpty: false,
      move: () => {
        this.valueChange$.next(`#${(<Instance>this.pickerInstance.get()).toHex()}`);
      },
      change: () => {
        this.valueChange$.next(`#${(<Instance>this.pickerInstance.get()).toHex()}`);
      },
    });
  }

  public ngOnDestroy() {
    this.pickerInstance.destroy();
  }

  public setValue(val: string) {
    this.pickerInstance.set(val);
  }
}
